import { ArrowPathIcon } from "@heroicons/react/24/outline";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { LoadingSpinner } from "./LoadingSpinner";
import { config } from "./constants";
import { useTimerSocket } from "./useTimerSocket";
import { gamesAPI, usersAPI, Game, User } from "./api";
import { useUser } from "@clerk/clerk-react";

const playWhistle = () => {
	new Audio("/whistle.mp3").play();
}

const URL = config.url;


export const GameView = () => {
	let { id } = useParams();
	const timerId = id ? parseInt(id) : 1;
	const { user } = useUser();

	// Use WebSocket hook for real-time timer updates
	const { timerDisplay, timerState, isConnected } = useTimerSocket(timerId);

	const [game, setGame] = useState<Game | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [showAddPlayer, setShowAddPlayer] = useState(false);
	const [selectedUserId, setSelectedUserId] = useState('');
	const [availableUsers, setAvailableUsers] = useState<User[]>([]);
	const [loadingUsers, setLoadingUsers] = useState(false);
	const [showDurationEdit, setShowDurationEdit] = useState(false);
	const [customDuration, setCustomDuration] = useState('');

	const running = timerState === 'running';

	// Keep screen awake using Wake Lock API
	useEffect(() => {
		let wakeLock: WakeLockSentinel | null = null;

		const requestWakeLock = async () => {
			try {
				if ('wakeLock' in navigator && navigator.wakeLock) {
					wakeLock = await navigator.wakeLock.request('screen');
					console.log('Screen wake lock acquired');
				}
			} catch (err) {
				console.error('Failed to acquire wake lock:', err);
			}
		};

		const handleVisibilityChange = () => {
			if (document.visibilityState === 'visible' && wakeLock === null) {
				requestWakeLock();
			}
		};

		// Request wake lock on mount
		requestWakeLock();

		// Re-request wake lock when page becomes visible again
		document.addEventListener('visibilitychange', handleVisibilityChange);

		return () => {
			// Release wake lock on unmount
			if (wakeLock) {
				wakeLock.release().then(() => {
					console.log('Screen wake lock released');
				});
			}
			document.removeEventListener('visibilitychange', handleVisibilityChange);
		};
	}, []);

	// Fetch game details
	useEffect(() => {
		const fetchGame = async () => {
			try {
				setIsLoading(true);
				setError(null);
				const fetchedGame = await gamesAPI.getGame(timerId);
				setGame(fetchedGame);
			} catch (err) {
				console.error('Failed to fetch game:', err);
				setError('Failed to load game. Please try again.');
			} finally {
				setIsLoading(false);
			}
		};

		fetchGame();
	}, [timerId]);

	// Fetch users when add player form is shown
	useEffect(() => {
		if (showAddPlayer && availableUsers.length === 0) {
			const fetchUsers = async () => {
				try {
					setLoadingUsers(true);
					const users = await usersAPI.getAll();
					setAvailableUsers(users);
				} catch (err) {
					console.error('Failed to fetch users:', err);
					alert('Failed to load users. Please try again.');
				} finally {
					setLoadingUsers(false);
				}
			};

			fetchUsers();
		}
	}, [showAddPlayer, availableUsers.length]);

	const teamAParticipant = game?.participations.find(p => p.role === 'team_a');
	const teamBParticipant = game?.participations.find(p => p.role === 'team_b');
	const currentUserParticipation = game?.participations.find(p => p.userId === user?.id);

	const getPlayerName = (participant: typeof teamAParticipant) => {
		if (!participant) return 'Waiting...';
		const user = participant.user;
		return user.username || user.firstName || user.id.substring(0, 8);
	};

	const getUserDisplayName = (user: User) => {
		if (user.username) return user.username;
		if (user.firstName && user.lastName) return `${user.firstName} ${user.lastName}`;
		if (user.firstName) return user.firstName;
		return user.id.substring(0, 12);
	};

	const handleAddHomeGoal = async () => {
		if (!game) return;
		try {
			const updatedGame = await gamesAPI.updateGame(game.id, {
				scoreTeamA: game.footyGame.scoreTeamA + 1,
				scoreTeamB: game.footyGame.scoreTeamB,
			});
			setGame(updatedGame);
			handlePauseTimer();
			playWhistle();
		} catch (err) {
			console.error('Failed to update score:', err);
			alert('Failed to update score. Please try again.');
		}
	};

	const handleAddAwayGoal = async () => {
		if (!game) return;
		try {
			const updatedGame = await gamesAPI.updateGame(game.id, {
				scoreTeamA: game.footyGame.scoreTeamA,
				scoreTeamB: game.footyGame.scoreTeamB + 1,
			});
			setGame(updatedGame);
			handlePauseTimer();
			playWhistle();
		} catch (err) {
			console.error('Failed to update score:', err);
			alert('Failed to update score. Please try again.');
		}
	};

	const handleJoinGame = async () => {
		if (!game || !user?.id) return;
		try {
			const updatedGame = await gamesAPI.joinGame(game.id, { userId: user.id });
			setGame(updatedGame);
		} catch (err) {
			console.error('Failed to join game:', err);
			alert('Failed to join game. Please try again.');
		}
	};

	const handleStartGame = async () => {
		if (!game) return;
		try {
			const updatedGame = await gamesAPI.startGame(game.id);
			setGame(updatedGame);
			handleStartTimer();
		} catch (err) {
			console.error('Failed to start game:', err);
			alert('Failed to start game. Please try again.');
		}
	};

	const handleCompleteGame = async () => {
		if (!game) return;
		try {
			const updatedGame = await gamesAPI.completeGame(game.id);
			setGame(updatedGame);
			handlePauseTimer();
		} catch (err) {
			console.error('Failed to complete game:', err);
			alert('Failed to complete game. Please try again.');
		}
	};

	const handleAbandonGame = async () => {
		if (!game) return;
		if (!window.confirm('Are you sure you want to abandon this game?')) return;

		try {
			await gamesAPI.abandonGame(game.id);
			window.location.href = '/';
		} catch (err) {
			console.error('Failed to abandon game:', err);
			alert('Failed to abandon game. Please try again.');
		}
	};

	const handleAddParticipant = async () => {
		if (!game || !selectedUserId) return;

		// Auto-detect which team slot is empty
		const teamAParticipant = game.participations.find(p => p.role === 'team_a');
		const teamBParticipant = game.participations.find(p => p.role === 'team_b');

		let roleToAssign: 'team_a' | 'team_b';
		if (!teamAParticipant) {
			roleToAssign = 'team_a';
		} else if (!teamBParticipant) {
			roleToAssign = 'team_b';
		} else {
			alert('Both teams are full!');
			return;
		}

		try {
			const updatedGame = await gamesAPI.addParticipant(game.id, {
				userId: selectedUserId,
				role: roleToAssign,
			});
			setGame(updatedGame);
			setSelectedUserId('');
			setShowAddPlayer(false);
		} catch (err) {
			console.error('Failed to add participant:', err);
			alert('Failed to add participant. Please try again.');
		}
	};

	const handleStartTimer = async () => {
		await fetch(`${URL}/timer/start/${id}`, {
			method: "post",
			headers: { "Content-Type": "application/json" },
		});
		playWhistle();
	};

	const handlePauseTimer = async () => {
		await fetch(`${URL}/timer/stop/${id}`, {
			method: "post",
			headers: { "Content-Type": "application/json" },
		});
		playWhistle();
	};

	const handleRestartTimer = async () => {
		await fetch(`${URL}/timer/reset/${id}`, {
			method: "post",
			headers: { "Content-Type": "application/json" },
		});
	};

	const handleUpdateDuration = async (milliseconds: number) => {
		if (!game) return;
		try {
			const updatedGame = await gamesAPI.updateGame(game.id, {
				duration: milliseconds,
			});
			setGame(updatedGame);
			// Reset the timer to reflect the new duration
			await handleRestartTimer();
			setShowDurationEdit(false);
			setCustomDuration('');
		} catch (err) {
			console.error('Failed to update duration:', err);
			alert('Failed to update duration. Please try again.');
		}
	};

	const handleQuickDuration = (minutes: number) => {
		handleUpdateDuration(minutes * 60 * 1000); // Convert minutes to milliseconds
	};

	const handleCustomDuration = () => {
		const minutes = parseInt(customDuration);
		if (isNaN(minutes) || minutes <= 0) {
			alert('Please enter a valid number of minutes');
			return;
		}
		handleUpdateDuration(minutes * 60 * 1000); // Convert minutes to milliseconds
	};

	const scoreBoard = () => {
		if (!game) return <LoadingSpinner />;

		return (
			<div className="flex w-full">
				<div className="flex w-full justify-start">
					<div className="flex flex-col">
						<div className="flex justify-center">
							<img
								className="h-10 w-10 rounded-full"
								src={teamAParticipant?.user.imageUrl || "/default-profile.svg"}
								alt=""
							/>
						</div>
						<div className="text-center text-sm">
							{getPlayerName(teamAParticipant)}
						</div>
					</div>
				</div>
				<div className="flex justify-center w-full text-2xl">
					<div className="text-4xl bold text-orange-600">
						{game.footyGame.scoreTeamA} : {game.footyGame.scoreTeamB}
					</div>
				</div>
				<div className="flex w-full justify-end">
					<div className="flex flex-col">
						<div className="flex justify-center">
							<img
								className="h-10 w-10 rounded-full"
								src={teamBParticipant?.user.imageUrl || "/default-profile.svg"}
								alt=""
							/>
						</div>
						<div className="text-center text-sm">
							{getPlayerName(teamBParticipant)}
						</div>
					</div>
				</div>
			</div>
		);
	};

	const timeClock = () => {
		const currentDurationMinutes = game ? Math.floor(game.footyGame.duration / 60000) : 0; // Convert milliseconds to minutes

		return (
			<div className="overflow-hidden rounded-lg bg-white shadow">
				<div className="px-4 py-5 sm:p-6">
					{/* Duration Settings */}
					<div className="mb-4 pb-4 border-b border-gray-200">
						<div className="flex justify-between items-center mb-2">
							<h3 className="text-sm font-semibold text-gray-700">
								Timer Duration: {currentDurationMinutes} min
							</h3>
							{!showDurationEdit && game?.status === 'PENDING' && (
								<button
									type="button"
									className="text-xs text-orange-600 hover:text-orange-500 font-medium"
									onClick={() => setShowDurationEdit(true)}
								>
									Change
								</button>
							)}
						</div>

						{showDurationEdit && (
							<div className="space-y-3">
								<div className="grid grid-cols-4 gap-2">
									<button
										type="button"
										className="rounded-md bg-gray-100 px-2 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-200"
										onClick={() => handleQuickDuration(2)}
									>
										2 min
									</button>
									<button
										type="button"
										className="rounded-md bg-gray-100 px-2 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-200"
										onClick={() => handleQuickDuration(3)}
									>
										3 min
									</button>
									<button
										type="button"
										className="rounded-md bg-gray-100 px-2 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-200"
										onClick={() => handleQuickDuration(5)}
									>
										5 min
									</button>
									<button
										type="button"
										className="rounded-md bg-gray-100 px-2 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-200"
										onClick={() => handleQuickDuration(10)}
									>
										10 min
									</button>
								</div>
								<div className="flex gap-2">
									<input
										type="number"
										placeholder="Custom (min)"
										className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-orange-500 focus:ring-orange-500 text-sm px-3 py-2 border"
										value={customDuration}
										onChange={(e) => setCustomDuration(e.target.value)}
										onKeyPress={(e) => {
											if (e.key === 'Enter') {
												handleCustomDuration();
											}
										}}
									/>
									<button
										type="button"
										className="rounded-md bg-orange-600 px-3 py-2 text-xs font-semibold text-white hover:bg-orange-500"
										onClick={handleCustomDuration}
									>
										Set
									</button>
								</div>
								<button
									type="button"
									className="w-full text-xs text-gray-600 hover:text-gray-500"
									onClick={() => {
										setShowDurationEdit(false);
										setCustomDuration('');
									}}
								>
									Cancel
								</button>
							</div>
						)}
					</div>

					{/* Timer Display */}
					<div className="w-full flex justify-between mb-2">
						<div></div>
						<div onClick={handleRestartTimer} className="cursor-pointer">
							<ArrowPathIcon className="h-6 text-gray-600 hover:text-gray-800" />
						</div>
					</div>
					<div className="flex justify-center">
						{!timerDisplay || timerDisplay === '--:--' ? (
							<h1 className="text-9xl"><LoadingSpinner /></h1>
						) : (
							<h1 className="text-9xl">{timerDisplay}</h1>
						)}
					</div>
					{!isConnected && (
						<div className="text-center text-sm text-red-600 mt-2">
							Disconnected - Reconnecting...
						</div>
					)}

					{/* Timer Controls */}
					<div className="flex justify-between w-full gap-4 mt-4">
						<button
							type="button"
							className="flex-1 rounded-md bg-orange-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-orange-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-600 disabled:opacity-75"
							onClick={handleStartTimer}
							disabled={running}
						>
							Start
						</button>
						<button
							type="button"
							className="flex-1 rounded-md bg-orange-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-orange-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-600 disabled:opacity-75"
							onClick={handlePauseTimer}
							disabled={!running}
						>
							Stop
						</button>
					</div>
				</div>
			</div>
		);
	};

	if (isLoading) {
		return (
			<div className="flex justify-center items-center py-12">
				<LoadingSpinner />
			</div>
		);
	}

	if (error || !game) {
		return (
			<div className="text-center py-12">
				<p className="text-red-600">{error || 'Game not found'}</p>
			</div>
		);
	}

	const canJoin = game.status === 'PENDING' && !currentUserParticipation && !teamBParticipant;
	const canAddPlayer = game.status === 'PENDING';
	const canStart = game.status === 'PENDING' && teamAParticipant && teamBParticipant;
	const canAbandonPending = game.status === 'PENDING';
	const canEnd = game.status === 'ACTIVE';
	const canScore = game.status === 'ACTIVE';

	return (
		<>
			<div className="divide-y divide-gray-200 overflow-hidden rounded-lg bg-white shadow">
				<div className="px-4 py-5 sm:p-6">{scoreBoard()}</div>

				{/* Game Status and Actions */}
				<div className="px-4 py-4 sm:px-6">
					<div className="mb-4 text-center">
						<span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${
							game.status === 'PENDING' ? 'bg-blue-50 text-blue-700 ring-blue-600/20' :
							game.status === 'ACTIVE' ? 'bg-gray-50 text-gray-600 ring-gray-500/10' :
							game.status === 'COMPLETED' ? 'bg-green-50 text-green-700 ring-green-600/20' :
							'bg-yellow-50 text-yellow-800 ring-yellow-600/20'
						}`}>
							{game.status}
						</span>
					</div>

					{/* Join Game Button */}
					{canJoin && (
						<button
							type="button"
							className="h-12 w-full mb-4 rounded-md bg-blue-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
							onClick={handleJoinGame}
						>
							Join Game
						</button>
					)}

					{/* Add Player Button/Form */}
					{canAddPlayer && !showAddPlayer && (
						<button
							type="button"
							className="h-12 w-full mb-4 rounded-md bg-cyan-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-cyan-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-600"
							onClick={() => setShowAddPlayer(true)}
						>
							Add Player Manually
						</button>
					)}

					{canAddPlayer && showAddPlayer && (
						<div className="mb-4 p-4 border border-gray-200 rounded-md">
							<h3 className="text-sm font-semibold mb-3">Add Player</h3>
							{loadingUsers ? (
								<div className="flex justify-center py-4">
									<LoadingSpinner />
								</div>
							) : (
								<div className="space-y-3">
									<div>
										<label htmlFor="selectUser" className="block text-xs font-medium text-gray-700 mb-1">
											Select Player
										</label>
										<select
											id="selectUser"
											className="block w-full rounded-md border-gray-300 shadow-sm focus:border-cyan-500 focus:ring-cyan-500 text-sm px-3 py-2 border"
											value={selectedUserId}
											onChange={(e) => setSelectedUserId(e.target.value)}
										>
											<option value="">-- Choose a player --</option>
											{availableUsers
												.filter(u => !game?.participations.some(p => p.userId === u.id))
												.map(user => (
													<option key={user.id} value={user.id}>
														{getUserDisplayName(user)}
													</option>
												))}
										</select>
									</div>
									<div className="flex gap-2">
										<button
											type="button"
											className="flex-1 rounded-md bg-cyan-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed"
											onClick={handleAddParticipant}
											disabled={!selectedUserId}
										>
											Add
										</button>
										<button
											type="button"
											className="flex-1 rounded-md bg-gray-200 px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-300"
											onClick={() => {
												setShowAddPlayer(false);
												setSelectedUserId('');
											}}
										>
											Cancel
										</button>
									</div>
								</div>
							)}
						</div>
					)}

					{/* Start Game Button */}
					{canStart && (
						<button
							type="button"
							className="h-12 w-full mb-4 rounded-md bg-green-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-green-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600"
							onClick={handleStartGame}
						>
							Start Game
						</button>
					)}

					{/* Goal Buttons (only for ACTIVE games) */}
					{canScore && (
						<>
							<button
								type="button"
								className="h-12 w-full rounded-md bg-orange-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-orange-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-600"
								onClick={handleAddHomeGoal}
							>
								Team A Goal
							</button>
							<button
								type="button"
								className="h-12 mt-4 w-full rounded-md bg-orange-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-orange-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-600"
								onClick={handleAddAwayGoal}
							>
								Team B Goal
							</button>
						</>
					)}

					{/* Complete Game Button */}
					{canEnd && (
						<button
							type="button"
							className="h-12 w-full mt-4 rounded-md bg-red-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600"
							onClick={handleCompleteGame}
						>
							End Game
						</button>
					)}

					{/* Abandon Game Button */}
					{canAbandonPending && (
						<button
							type="button"
							className="h-12 w-full mt-4 rounded-md bg-gray-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600"
							onClick={handleAbandonGame}
						>
							Abandon Game
						</button>
					)}
				</div>
			</div>
			<div className="mt-4">{timeClock()}</div>
		</>
	);
};
